/**
 * /onboarding/compliance — Compliance Framework Setup
 *
 * Shown after /onboarding/roles and before /billing/select-plan.
 * The new OrgAdmin selects which compliance frameworks should be
 * active for their organization.  All three default to OFF — the user
 * must explicitly opt in.
 *
 * After saving, the user is routed to /billing/select-plan.
 */

import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ShieldCheck, BookOpen, Eye, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

type FrameworkItem = {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
};

type SetupResponse = {
  frameworks: FrameworkItem[];
};

// Visual metadata for the three onboarding frameworks.
const FRAMEWORK_META: Record<string, { icon: typeof ShieldCheck }> = {
  hipaa:   { icon: ShieldCheck },
  ferpa:   { icon: BookOpen    },
  ada_wcag:{ icon: Eye         },
};

function frameworkIcon(key: string) {
  return FRAMEWORK_META[key]?.icon ?? ShieldCheck;
}

export default function OnboardingCompliancePage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const { data, isLoading, isError } = useQuery<SetupResponse>({
    queryKey: ["/api/organization/compliance-setup"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/organization/compliance-setup");
      if (!res.ok) throw new Error("Failed to load compliance frameworks.");
      return res.json();
    },
  });

  // Local toggle state — initialised from server response.
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [initialised, setInitialised] = useState(false);

  if (data && !initialised) {
    const init: Record<string, boolean> = {};
    for (const fw of data.frameworks) init[fw.key] = fw.enabled;
    setEnabled(init);
    setInitialised(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (selections: { key: string; enabled: boolean }[]) => {
      const res = await apiRequest("POST", "/api/organization/compliance-setup", {
        frameworks: selections,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? "Failed to save compliance setup.");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Compliance setup saved",
        description: "Your organization's compliance frameworks have been configured.",
      });
      navigate("/billing/select-plan", { replace: true });
    },
    onError: (err: Error) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  function toggle(key: string) {
    setEnabled(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function buildSelections(overrideAll?: boolean) {
    return (data?.frameworks ?? []).map(fw => ({
      key:     fw.key,
      enabled: overrideAll !== undefined ? overrideAll : (enabled[fw.key] ?? false),
    }));
  }

  function handleContinue() {
    if (!data) return;
    saveMutation.mutate(buildSelections());
  }

  function handleSkip() {
    // Skipping saves all frameworks as OFF — user can enable them later from Compliance Center.
    if (!data) { navigate("/billing/select-plan", { replace: true }); return; }
    saveMutation.mutate(buildSelections(false));
  }

  const frameworks = data?.frameworks ?? [];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Set up compliance frameworks
          </h1>
          <p className="text-muted-foreground text-sm">
            Choose which compliance frameworks your organization needs. You can
            adjust this anytime from the Compliance Center.
          </p>
        </div>

        {/* Framework toggles */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Available frameworks</CardTitle>
            <CardDescription>
              Enable the frameworks that apply to your organization. All are off
              by default — turn on only what you need.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))
            ) : isError ? (
              <p className="text-sm text-destructive">
                Failed to load frameworks. Please refresh.
              </p>
            ) : (
              frameworks.map(fw => {
                const Icon    = frameworkIcon(fw.key);
                const isOn    = enabled[fw.key] ?? false;
                const switchId = `fw-${fw.key}`;
                return (
                  <div
                    key={fw.key}
                    className="flex items-start justify-between gap-4 py-1"
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <Icon className="h-4 w-4 flex-shrink-0 mt-0.5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <Label
                          htmlFor={switchId}
                          className="font-medium cursor-pointer"
                        >
                          {fw.name}
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {fw.description}
                        </p>
                      </div>
                    </div>
                    <Switch
                      id={switchId}
                      checked={isOn}
                      onCheckedChange={() => toggle(fw.key)}
                      aria-label={`Toggle ${fw.name}`}
                    />
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleContinue}
            disabled={isLoading || saveMutation.isPending}
            className="flex-1"
          >
            {saveMutation.isPending ? "Saving…" : "Continue"}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            disabled={saveMutation.isPending}
            className="text-muted-foreground"
          >
            Skip for now
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          You can enable or configure additional compliance frameworks from the
          Compliance Center at any time.
        </p>
      </div>
    </div>
  );
}
